// S3 left pane: the indented component tree. Root lines are selectable (they map
// to the editable draft lines); phantoms the explosion inlined expand inline
// with a greyed "phantom" badge (§3.3.7). Deeper nodes are read-only structural
// context. Pure presentation over the reconstructed tree (helpers.buildBomTree).

import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { BomTreeNode } from "./helpers";

export function BomTree({
  nodes,
  selectedLineNo,
  onSelectLine,
  uomLabel,
}: {
  nodes: readonly BomTreeNode[];
  selectedLineNo: number | null;
  onSelectLine: (lineNo: number) => void;
  uomLabel: (uomId: string | null) => string;
}) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());

  const toggle = (key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const renderNode = (node: BomTreeNode, depth: number) => {
    const hasChildren = node.children.length > 0;
    const isCollapsed = collapsed.has(node.key);
    const selectable = node.rootLineNo !== null;
    const selected = selectable && node.rootLineNo === selectedLineNo;
    const sku = node.sku ?? t("catalogue.bomEditor.tree.unknownComponent");
    const qty =
      node.qtyNet !== null
        ? `${formatQty(node.qtyNet)} ${uomLabel(node.uomId)} · ${t("catalogue.bomEditor.tree.netOfScrap")}`
        : null;
    return (
      <li key={node.key} className="bom-tree-item">
        <div
          className={`bom-tree-row${selected ? " is-selected" : ""}${node.isPhantom ? " is-phantom" : ""}`}
          style={{ paddingInlineStart: `${depth * 1.1 + 0.5}rem` }}
        >
          {hasChildren ? (
            <button
              type="button"
              className="bom-tree-toggle"
              aria-expanded={!isCollapsed}
              aria-label={
                isCollapsed
                  ? t("catalogue.bomEditor.tree.expand")
                  : t("catalogue.bomEditor.tree.collapse")
              }
              onClick={() => {
                toggle(node.key);
              }}
            >
              {isCollapsed ? "▸" : "▾"}
            </button>
          ) : (
            <span className="bom-tree-toggle bom-tree-toggle-empty" aria-hidden="true" />
          )}
          {selectable ? (
            <button
              type="button"
              className="bom-tree-label bom-tree-label-button"
              aria-pressed={selected}
              onClick={() => {
                if (node.rootLineNo !== null) onSelectLine(node.rootLineNo);
              }}
            >
              <TreeLabel sku={sku} node={node} qty={qty} />
            </button>
          ) : (
            <span className="bom-tree-label">
              <TreeLabel sku={sku} node={node} qty={qty} />
            </span>
          )}
        </div>
        {hasChildren && !isCollapsed ? (
          <ul className="bom-tree-children">
            {node.children.map((child) => renderNode(child, depth + 1))}
          </ul>
        ) : null}
      </li>
    );
  };

  return (
    <ul className="bom-tree" data-testid="bom-tree">
      {nodes.map((node) => renderNode(node, 0))}
    </ul>
  );
}

function TreeLabel({ sku, node, qty }: { sku: string; node: BomTreeNode; qty: string | null }) {
  const { t } = useTranslation();
  return (
    <>
      <span className="bom-tree-sku">{sku}</span>
      {node.isPhantom ? (
        <span className="bom-tree-badge bom-tree-badge-phantom">
          {t("catalogue.bomEditor.phantom")}
        </span>
      ) : null}
      {node.isOptional ? (
        <span className="bom-tree-badge bom-tree-badge-optional">
          {t("catalogue.bomEditor.inspector.optional")}
        </span>
      ) : null}
      {qty !== null ? <span className="bom-tree-qty">{qty}</span> : null}
    </>
  );
}

function formatQty(value: number): string {
  // Trim to a sensible precision without trailing-zero noise.
  return Number.isInteger(value) ? String(value) : value.toFixed(6).replace(/\.?0+$/, "");
}
